import { Paper } from '@mui/material'
import { styled } from '@mui/system'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Loader from '../../components/Loader/Loader'
import EventTagSelect from '../../components/events/EventTagSelect'
import Toptag from '../../components/toptag/Toptag'
import LocationDetailsCard from '../../components/events/LocationDetailsCard'
import { useGooglePlaceMap } from '../../hooks/useGooglePlaceMap'
import apiFunctions from '../../api/apiFunctions'
import { hasValidCoords } from '../../utils/eventTimezone'
import '../../components/events/eventForm.css'

const NavPaper = styled(Paper)({
    padding: '0',
    borderRadius: '12px',
    backgroundColor: 'white',
    boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px',
    overflow: 'hidden',
})

const EMPTY_OBJ = {
    title: '',
    place: '',
    latitude: 0,
    longitude: 0,
    map_url: '',
    country: '',
    country_code: '',
    state_code: '',
    world_rank: 0,
    required_people: 100,
    annual_emission_of_co2: '',
    sector_of_pollution: '',
    source_id: '',
    sector_rank: '',
    event_type: 'Normal',
    event_tag: [],
    special_notes: '',
}

export default function NormalEvent() {
    const [isLoading, setIsloading] = useState(false)
    const navigate = useNavigate()
    const [err, setErr] = useState(false)
    const [obj, setObj] = useState(EMPTY_OBJ)
    const mapRef = useRef(null)
    const inputRef = useRef(null)
    const [eventTag, setEventTag] = useState([])

    const blockNonInteger = (e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault() }
    const blockNonDecimal = (e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault() }

    const onPlaceSelected = useCallback((data) => {
        setObj((prev) => ({
            ...prev,
            place: data.place,
            latitude: data.latitude,
            longitude: data.longitude,
            map_url: data.map_url,
            country: data.country,
            country_code: data.country_code,
            state: data.state,
            state_code: data.state_code,
        }))
        setErr(false)
    }, [])

    const { confirmedPlace } = useGooglePlaceMap({ mapRef, inputRef, onPlaceSelected })

    const getTags = async () => {
        const result = await apiFunctions.getAllTag(100, '', 1)
        if (!result.data.status) {
            if (result.data.code === 401) {
                localStorage.clear()
                navigate('/login')
                toast.info('Session Expired Please login again!!')
            }
            toast.error(result.data.message, { position: toast.POSITION.TOP_RIGHT })
        } else {
            setEventTag(result.data.data)
            setIsloading(false)
        }
    }

    useEffect(() => {
        getTags()
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        const isInvalid = (
            obj.title === ''
            || obj.place === ''
            || !obj.country_code
            || obj.annual_emission_of_co2 === ''
            || obj.source_id === ''
            || obj.sector_rank === ''
            || !Array.isArray(obj.event_tag)
            || obj.event_tag.length === 0
            || !hasValidCoords(obj.latitude, obj.longitude)
            || obj.place !== confirmedPlace
        )

        if (isInvalid) {
            setErr(true)
            return
        }

        setErr(false)
        const userObj = {
            title: obj.title,
            place: obj.place,
            latitude: obj.latitude,
            longitude: obj.longitude,
            map_url: obj.map_url,
            country: obj.country,
            world_rank: obj.world_rank,
            required_people: obj.required_people,
            annual_emission_of_co2: obj.annual_emission_of_co2,
            sector_of_pollution: obj.sector_of_pollution,
            source_id: obj.source_id,
            sector_rank: obj.sector_rank,
            event_type: obj.event_type,
            tag: obj.event_tag,
            special_notes: obj.special_notes,
            country_code: obj.country_code,
            state_code: obj.state_code,
        }

        const result = await apiFunctions.createEvent(userObj)
        if (!result.data.status) {
            if (result.data.code === 401) {
                localStorage.clear()
                navigate('/login')
                toast.info('Session Expired Please login again!!')
            }
            toast.error(result.data.message, { position: toast.POSITION.TOP_RIGHT })
        } else {
            setObj(EMPTY_OBJ)
            toast.success('Event created succesfully!')
            setErr(false)
        }
    }

    const addUserData = (e) => {
        setErr(false)
        const { name, value } = e.target
        if (name === 'world_rank' || name === 'required_people' || name === 'sector_rank') {
            setObj((prev) => ({ ...prev, [name]: parseInt(value, 10) || 0 }))
        } else {
            setObj((prev) => ({ ...prev, [name]: value }))
        }
    }

    return (
        <>
            {isLoading ? <Loader /> : (
                <>
                    <Toptag />
                    <ToastContainer autoClose={1000} />
                    <NavPaper>
                        <form className="event-form-shell" onSubmit={handleSubmit}>
                            <div className="event-form-title">New Normal Event</div>
                            <div className="event-form-subtitle">
                                Search for a location on the map. Country and region codes are filled automatically from Google Places.
                            </div>

                            <div className="event-form-map" id="map" ref={mapRef} />

                            <div className="event-form-section">
                                <div className="event-form-section-title">Location</div>
                                <div className="event-form-grid">
                                    <div className="event-form-field event-form-field--full">
                                        <label htmlFor="place">Place</label>
                                        <input
                                            type="text"
                                            ref={inputRef}
                                            placeholder="Search event location"
                                            name="place"
                                            id="place"
                                            onChange={addUserData}
                                            value={obj.place}
                                        />
                                        {err && (obj.place === '' || obj.place !== confirmedPlace) ? (
                                            <div className="event-form-error">
                                                {obj.place === '' ? 'Required!' : 'Please select a valid place from suggestions!'}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="event-form-field event-form-field--full">
                                        <LocationDetailsCard location={obj} />
                                        {err && !obj.country_code ? (
                                            <div className="event-form-error">Select a place with a valid country from Google Maps.</div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className="event-form-section">
                                <div className="event-form-section-title">Event details</div>
                                <div className="event-form-grid">
                                    <div className="event-form-field">
                                        <label htmlFor="title">Event title</label>
                                        <input type="text" name="title" id="title" onChange={addUserData} value={obj.title} />
                                        {err && obj.title === '' ? <div className="event-form-error">Required!</div> : null}
                                    </div>
                                    <div className="event-form-field">
                                        <label htmlFor="source_id">Source ID</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="numeric"
                                            name="source_id"
                                            id="source_id"
                                            onKeyDown={blockNonInteger}
                                            onChange={(e) => addUserData({ target: { name: 'source_id', value: e.target.value.replace(/[^\d]/g, '') } })}
                                            value={obj.source_id}
                                        />
                                        {err && obj.source_id === '' ? <div className="event-form-error">Required!</div> : null}
                                    </div>
                                    <div className="event-form-field">
                                        <label htmlFor="sector_of_pollution">Sector of pollution</label>
                                        <input type="text" name="sector_of_pollution" id="sector_of_pollution" onChange={addUserData} value={obj.sector_of_pollution} />
                                    </div>
                                    <div className="event-form-field">
                                        <label htmlFor="sector_rank">Sector rank</label>
                                        <input type="number" min="0" step="1" inputMode="numeric" onKeyDown={blockNonInteger} name="sector_rank" id="sector_rank" onChange={addUserData} value={obj.sector_rank} />
                                        {err && obj.sector_rank === '' ? <div className="event-form-error">Required!</div> : null}
                                    </div>
                                    <div className="event-form-field">
                                        <label htmlFor="world_rank">Country world rank</label>
                                        <input type="number" min="0" step="1" inputMode="numeric" onKeyDown={blockNonInteger} name="world_rank" id="world_rank" onChange={addUserData} value={obj.world_rank} />
                                    </div>
                                    <div className="event-form-field">
                                        <label htmlFor="required_people">Required people</label>
                                        <input type="number" min="0" step="1" inputMode="numeric" onKeyDown={blockNonInteger} name="required_people" id="required_people" onChange={addUserData} value={obj.required_people} />
                                    </div>
                                    <div className="event-form-field">
                                        <label htmlFor="annual_emission_of_co2">Annual emission of CO2</label>
                                        <input type="number" min="0" inputMode="decimal" onKeyDown={blockNonDecimal} name="annual_emission_of_co2" id="annual_emission_of_co2" onChange={addUserData} value={obj.annual_emission_of_co2} />
                                        {err && obj.annual_emission_of_co2 === '' ? <div className="event-form-error">Required!</div> : null}
                                    </div>
                                    <div className="event-form-field event-form-field--full">
                                        <label htmlFor="special_notes">Special notes</label>
                                        <input type="text" name="special_notes" id="special_notes" onChange={addUserData} value={obj.special_notes} />
                                    </div>
                                    <div className="event-form-field event-form-field--full">
                                        <label>Event tags</label>
                                        <EventTagSelect
                                            isMulti
                                            name="event_tag"
                                            hasError={err && obj.event_tag.length === 0}
                                            options={eventTag.map((tag) => ({ value: tag._id, label: tag.title }))}
                                            value={eventTag
                                                .filter((tag) => obj.event_tag.includes(tag._id))
                                                .map((tag) => ({ value: tag._id, label: tag.title }))}
                                            onChange={(selectedOptions) => {
                                                setObj((prev) => ({
                                                    ...prev,
                                                    event_tag: selectedOptions ? selectedOptions.map((o) => o.value) : [],
                                                }))
                                                setErr(false)
                                            }}
                                            placeholder="Select tags..."
                                        />
                                        {err && obj.event_tag.length === 0 ? (
                                            <div className="event-form-error">At least one tag is required!</div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className="event-form-actions">
                                <button className="event-form-btn" type="submit">Create event</button>
                            </div>
                        </form>
                    </NavPaper>
                </>
            )}
        </>
    )
}
